import * as React from 'react';
import { useMemo, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@mui/material/Button';
import { IOutletDefaultFormValues, IOutletFormValues, outletFormSchema } from '../../../../@types/model/masterData/outlet/outletFormValues';
import { useAppSelector } from '../../../../@types/redux';
import { PillButton, ControlledTextField, ControlledMuiAutoCompleteSelect } from '@zz2/zz2-ui';
import { ControlledLabelledCheckBox } from '../../../../components/input/form/ControlledLabelledCheckBoxWrapper';
import { IOptionType } from '../../../../@types/helper';
import CustomReactHookFormDevTools from '../../../../components/devtools/ReactHookFormDevTools';

interface IOutletFormProps {
    isLoading : boolean;
    initialValues : IOutletFormValues | IOutletDefaultFormValues;
    onSubmit : (values : IOutletFormValues) => void;
    onClose : () => void;
}

const OutletForm = (props : IOutletFormProps) : React.ReactElement => {
    const schema = outletFormSchema;
    const { initialValues, onSubmit, onClose, isLoading } = props;

    const { handleSubmit, control, formState: { isValid, isDirty }, watch, setValue } = useForm<IOutletFormValues>({
        defaultValues: initialValues,
        resolver: zodResolver(schema),
        mode: 'onChange',
    });

    const auth = useAppSelector(state => state.auth);
    const packLines = useAppSelector(state => state.masterData.packLines);
    const printServers = useAppSelector(state => state.masterData.printServers);
    const printers = useAppSelector(state => state.masterData.printers);
    const reports = useAppSelector(state => state.masterData.reports);
    const sites = useAppSelector(state => state.masterData.sites);
    const inventories = useAppSelector(state => state.masterData.inventories);

    const watchedPackLine = watch('packLine');
    const watchedDefaultPrintServer = watch('defaultPrintServer');

    const packLineOptions : Array<IOptionType> = useMemo(() => {
        return packLines?.filter(x => x.isActive && auth.session?.user?.siteIds.some(y => y === x.siteId))
            .map(x => ({ label: `(${x.code})  ${x.name}`, value: x.id })) ?? [];
    }, [packLines, auth.session?.user?.siteIds]);

    const siteOptions : Array<IOptionType> = useMemo(() => {
        return sites?.filter(x => x.isActive && x.organizationIds.some(y => auth.session?.user.organizationIds.some(z => z === y)))
            .map(x => ({ label: `(${x.code}) ${x.description}`, value: x.id })) ?? [];
    }, [sites, auth.session?.user.organizationIds]);

    const inventoryOptions : Array<IOptionType> = useMemo(() => {
        return inventories?.filter(x => x.isActive)
            .map(x => ({ label: `(${x.code}) ${x.description}`, value: x.id })) ?? [];
    }, [inventories]);

    const printServerOptions : Array<IOptionType> = useMemo(() => {
        if (!watchedPackLine || !packLines || !printServers) return [];
        const selectedPackLineDetails = packLines.find(pl => pl.id === watchedPackLine.value);
        if (!selectedPackLineDetails) return [];
        return printServers
            .filter(ps => ps.isActive && ps.siteId === selectedPackLineDetails.siteId)
            .map(ps => ({ label: ps.name, value: ps.id }));
    }, [watchedPackLine, packLines, printServers]);

    const printerOptions : Array<IOptionType> = useMemo(() => {
        if (!watchedDefaultPrintServer || !printers) return [];
        return printers
            .filter(p => p.isActive && p.printServerId === watchedDefaultPrintServer.value)
            .map(p => ({ label: p.name, value: p.id }));
    }, [watchedDefaultPrintServer, printers]);

    const reportOptions : Array<IOptionType> = useMemo(() => {
        if (!watchedDefaultPrintServer || !reports) return [];
        return reports
            .filter(r => r.isActive && r.printServerId === watchedDefaultPrintServer.value)
            .map(r => ({ label: r.name, value: r.id }));
    }, [watchedDefaultPrintServer, reports]);

    // Effect for when packLine changes
    useEffect(() => {
        // Check if it's not the initial load causing this effect for an existing value
        if (initialValues.packLine?.value !== watchedPackLine?.value) {
            setValue('defaultPrintServer', undefined, { shouldValidate: true, shouldDirty: true });
            setValue('defaultPrinter', undefined, { shouldValidate: true, shouldDirty: true });
            setValue('defaultReport', undefined, { shouldValidate: true, shouldDirty: true });
        }
    }, [watchedPackLine, setValue, initialValues.packLine]);

    // Effect for when defaultPrintServer changes
    useEffect(() => {
        if (initialValues.defaultPrintServer?.value !== watchedDefaultPrintServer?.value) {
            setValue('defaultPrinter', undefined, { shouldValidate: true, shouldDirty: true });
            setValue('defaultReport', undefined, { shouldValidate: true, shouldDirty: true });
        }
    }, [watchedDefaultPrintServer, setValue, initialValues.defaultPrintServer]);

    return (
        <div>
            <CustomReactHookFormDevTools control={control} />
            <form onSubmit={handleSubmit(onSubmit)} className={'p20'}>
                <div className={'fdc aic'}>
                    <ControlledTextField
                        className={'mb10 w500'}
                        name={'code'}
                        label={'Code'}
                        control={control}
                        defaultValue={initialValues.code} />
                    <ControlledTextField
                        className={'mb10 w500'}
                        name={'deviceMacAddress'}
                        label={'Device MAC Address'}
                        control={control}
                        defaultValue={initialValues.deviceMacAddress} />
                    <ControlledMuiAutoCompleteSelect
                        className={'p0 mb10 w500'}
                        name={'packLine'}
                        label={'Pack Line'}
                        control={control}
                        options={packLineOptions}
                        defaultValues={initialValues.packLine ?? null} />
                    <ControlledMuiAutoCompleteSelect
                        className={'p0 mb10 w500'}
                        name={'defaultPrintServer'}
                        label={'Default Print Server'}
                        control={control}
                        options={printServerOptions}
                        defaultValues={initialValues.defaultPrintServer ?? null}
                        disabled={!watchedPackLine} />
                    <ControlledMuiAutoCompleteSelect
                        className={'p0 mb10 w500'}
                        name={'defaultPrinter'}
                        label={'Default Printer'}
                        control={control}
                        options={printerOptions}
                        defaultValues={initialValues.defaultPrinter ?? null}
                        disabled={!watchedDefaultPrintServer} />
                    <ControlledMuiAutoCompleteSelect
                        className={'p0 mb10 w500'}
                        name={'defaultReport'}
                        label={'Default Report'}
                        control={control}
                        options={reportOptions}
                        defaultValues={initialValues.defaultReport ?? null}
                        disabled={!watchedDefaultPrintServer} />
                    <ControlledMuiAutoCompleteSelect
                        className={'p0 mb10 w500'}
                        name={'defaultExporterSite'}
                        label={'Default Exporter Site'}
                        control={control}
                        options={siteOptions}
                        defaultValues={initialValues.defaultExporterSite ?? null} />
                    <ControlledMuiAutoCompleteSelect
                        className={'p0 mb10 w500'}
                        name={'defaultInventory'}
                        label={'Default Inventory'}
                        control={control}
                        options={inventoryOptions}
                        defaultValues={initialValues.defaultInventory ?? null} />
                    <div className={'fdr jcfs mb5'}>
                        <ControlledLabelledCheckBox
                            className={'p0'}
                            name={'isActive'}
                            label={'Is Active'}
                            control={control}
                            defaultValue={initialValues.isActive} />
                    </div>
                    <div className={'fdr ml10 ais jcfe pt10 pb10'}>
                        <Button className={'cpd mr20'} type={'button'} onClick={onClose}>
                            CANCEL
                        </Button>
                        <PillButton
                            disabled={!isDirty || !isValid || isLoading}
                            className={'ml15 pl20 pr20 h35'}
                            text={'Save'}
                            type={'submit'}
                            color={'secondary'} />
                    </div>
                </div>
            </form>
        </div>
    );
};

export default OutletForm;
